// apps/web/src/lib/vendas/historico.ts — Fase 12, Plano 31 (PV-07, T-12-50).
// Apresentação pura do Histórico do PV: eventos `pv.*` já rotulados pela API (pt-BR). A tela só formata
// data/hora, resume o que mudou (antes → depois) e decide quando o motivo longo pede "Ver mais".
import type { EventoHistoricoPv } from '@pieltec/contracts';
import { formatarDataHora, formatarMoeda } from './formatacao';

/** Acima disto o motivo não cabe em duas linhas: a tela mostra "Ver mais" (line-clamp-2). */
export const LIMITE_JUSTIFICATIVA_RESUMIDA = 140;
export const AUTOR_SISTEMA = 'Sistema';

export { formatarDataHora };

/** Motivo longo ou com várias linhas não cabe em `line-clamp-2`: mostra "Ver mais". */
export function precisaVerMais(texto: string | null | undefined): boolean {
  if (!texto) return false;
  return texto.length > LIMITE_JUSTIFICATIVA_RESUMIDA || texto.split(/\r?\n/).length > 2;
}

type Formatador = (valor: unknown) => string;

const TRACO = '—';

function texto(valor: unknown): string {
  if (typeof valor === 'string') return valor === '' ? TRACO : valor;
  if (typeof valor === 'number' || typeof valor === 'boolean') return String(valor);
  return TRACO;
}

function dataCurta(valor: unknown): string {
  if (typeof valor !== 'string' || valor === '') return TRACO;
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor);
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : valor;
}

const ROTULO_PRIORIDADE: Record<string, string> = { '0': 'Normal', '10': 'Alta', '20': 'Urgente' };

/** Campos do `before`/`after` que a tela sabe explicar (os demais ficam só na auditoria). */
const CAMPOS_RESUMIVEIS: ReadonlyArray<{ chave: string; rotulo: string; formatar: Formatador }> = [
  { chave: 'codigoItemCliente', rotulo: 'Código do item', formatar: texto },
  { chave: 'descricao', rotulo: 'Descrição', formatar: texto },
  { chave: 'cor', rotulo: 'Cor', formatar: texto },
  { chave: 'quantidade', rotulo: 'Quantidade', formatar: texto },
  { chave: 'unidade', rotulo: 'Unidade', formatar: texto },
  { chave: 'precoUnitario', rotulo: 'Preço unitário', formatar: (v) => (typeof v === 'number' ? formatarMoeda(v, 4) : texto(v)) },
  { chave: 'previsaoEntrega', rotulo: 'Prazo', formatar: dataCurta },
  { chave: 'prioridade', rotulo: 'Prioridade', formatar: (v) => ROTULO_PRIORIDADE[texto(v)] ?? texto(v) },
];

function comoObjeto(valor: unknown): Record<string, unknown> {
  return valor !== null && typeof valor === 'object' && !Array.isArray(valor) ? (valor as Record<string, unknown>) : {};
}

/** Linhas "Campo: antes → depois" só para o que mudou de fato; vazio quando o evento não traz esses campos. */
export function resumirMudancas(detalhe: EventoHistoricoPv['detalhe']): string[] {
  if (!detalhe) return [];
  const antes = comoObjeto(detalhe.antes);
  const depois = comoObjeto(detalhe.depois);
  const linhas: string[] = [];
  for (const { chave, rotulo, formatar } of CAMPOS_RESUMIVEIS) {
    if (!(chave in depois)) continue;
    const valorDepois = depois[chave];
    const valorAntes = antes[chave];
    if (valorAntes === valorDepois) continue;
    linhas.push(`${rotulo}: ${formatar(valorAntes)} → ${formatar(valorDepois)}`);
  }
  const numeroItem = depois.numeroItem ?? antes.numeroItem;
  return (typeof numeroItem === 'number' || typeof numeroItem === 'string') && linhas.length > 0 ? [`Item ${numeroItem}`, ...linhas] : linhas;
}

export interface EventoHistoricoApresentado {
  readonly id: string;
  readonly rotulo: string;
  readonly autor: string;
  readonly dataHora: string;
  readonly justificativa: string | null;
  readonly verMais: boolean;
  readonly mudancas: readonly string[];
}

export function formatarEventoHistorico(evento: EventoHistoricoPv): EventoHistoricoApresentado {
  return {
    id: evento.id,
    rotulo: evento.rotulo,
    autor: evento.actorNome ?? AUTOR_SISTEMA,
    dataHora: formatarDataHora(evento.createdAt),
    justificativa: evento.justificativa?.trim() ? evento.justificativa : null,
    verMais: precisaVerMais(evento.justificativa),
    mudancas: resumirMudancas(evento.detalhe),
  };
}

/** Quantas páginas de 50 existem (mínimo 1) — a API devolve `total` e `tamanhoPagina`. */
export function totalPaginasHistorico(total: number, tamanhoPagina: number): number {
  return tamanhoPagina > 0 ? Math.max(1, Math.ceil(total / tamanhoPagina)) : 1;
}
